/**
 * Foundations — colour palette, typography scale, spacing, radius, shadows.
 *
 * Typography section shows both options:
 * - Option 1: Plus Jakarta Sans throughout (current default)
 * - Option 2: Instrument Sans for display/headings (toggle to preview)
 */
import { useState } from 'react';
import '@fontsource-variable/instrument-sans';
import { Section } from '../shared';

// ─── Swatch row ───────────────────────────────────────────────────────────────

function Swatch({ token, label }: { token: string; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: `var(${token})`,
          border: '1px solid var(--color-surface-border)',
          flexShrink: 0,
        }}
      />
      <div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-text)',
            letterSpacing: '-0.01em',
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-mono)',
            marginTop: 1,
          }}
        >
          {token}
        </p>
      </div>
    </div>
  );
}

function SwatchGroup({
  label,
  swatches,
}: {
  label: string;
  swatches: { token: string; label: string }[];
}) {
  return (
    <div>
      <p
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-text-muted)',
          marginBottom: 12,
        }}
      >
        {label}
      </p>
      <div className="flex flex-wrap gap-4">
        {swatches.map((s) => (
          <Swatch key={s.token} {...s} />
        ))}
      </div>
    </div>
  );
}

// ─── Type scale ───────────────────────────────────────────────────────────────

const TYPE_SCALE: {
  label: string;
  role: string;
  token: string;
  size: string;
  weight: number;
  tracking: string;
  leading: number;
  useDisplay?: boolean;
}[] = [
  {
    label: 'Display',
    role: 'Hero numbers, milestone banners',
    token: '--text-4xl',
    size: 'var(--text-4xl)',
    weight: 800,
    tracking: '-0.03em',
    leading: 1.1,
    useDisplay: true,
  },
  {
    label: 'Heading 1',
    role: 'Page titles, course names',
    token: '--text-3xl',
    size: 'var(--text-3xl)',
    weight: 700,
    tracking: '-0.025em',
    leading: 1.2,
    useDisplay: true,
  },
  {
    label: 'Heading 2',
    role: 'Section headings, modal titles',
    token: '--text-2xl',
    size: 'var(--text-2xl)',
    weight: 700,
    tracking: '-0.02em',
    leading: 1.25,
    useDisplay: true,
  },
  {
    label: 'Heading 3',
    role: 'Card headings, sidebar titles',
    token: '--text-xl',
    size: 'var(--text-xl)',
    weight: 600,
    tracking: '-0.015em',
    leading: 1.35,
    useDisplay: true,
  },
  {
    label: 'Subtitle',
    role: 'Lead copy, feature descriptions',
    token: '--text-lg',
    size: 'var(--text-lg)',
    weight: 500,
    tracking: '-0.005em',
    leading: 1.4,
  },
  {
    label: 'Body',
    role: 'Descriptions, explainer text',
    token: '--text-base',
    size: 'var(--text-base)',
    weight: 400,
    tracking: '0em',
    leading: 1.65,
  },
  {
    label: 'Small',
    role: 'Secondary labels, list item copy',
    token: '--text-sm',
    size: 'var(--text-sm)',
    weight: 400,
    tracking: '0em',
    leading: 1.55,
  },
  {
    label: 'Caption',
    role: 'Timestamps, counts, helper hints',
    token: '--text-xs',
    size: 'var(--text-xs)',
    weight: 500,
    tracking: '0.01em',
    leading: 1.4,
  },
];

// ─── Product text roles ────────────────────────────────────────────────────────

const PRODUCT_ROLES: {
  role: string;
  sample: string;
  style: React.CSSProperties;
  desc: string;
}[] = [
  {
    role: 'Page Title',
    sample: 'Introduction to Algorithms',
    style: {
      fontFamily: 'var(--font-display)',
      fontSize: 'var(--text-3xl)',
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: 1.2,
      color: 'var(--color-text)',
    },
    desc: 'display font · 700 · −0.025em',
  },
  {
    role: 'Section Heading',
    sample: 'My Courses',
    style: {
      fontFamily: 'var(--font-display)',
      fontSize: 'var(--text-xl)',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.35,
      color: 'var(--color-text)',
    },
    desc: 'display font · 600 · −0.015em',
  },
  {
    role: 'Eyebrow / Overline',
    sample: 'Course · 12 Lessons',
    style: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--color-text-muted)',
    },
    desc: '11px · 600 · +0.08em · uppercase',
  },
  {
    role: 'Card Title',
    sample: 'Quantum Mechanics',
    style: {
      fontSize: 15,
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
      color: 'var(--color-text)',
    },
    desc: '15px · 600 · −0.01em',
  },
  {
    role: 'Metadata',
    sample: '8 of 24 lessons · 42% complete',
    style: {
      fontSize: 13,
      fontWeight: 450,
      lineHeight: 1.4,
      color: 'var(--color-text-muted)',
    },
    desc: '12px · 450 · 0em',
  },
  {
    role: 'Helper Text',
    sample: 'You can change this in your settings at any time.',
    style: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.5,
      color: 'var(--color-text-secondary)',
    },
    desc: '12px · 400 · 0em',
  },
  {
    role: 'Utility / Chip',
    sample: 'In Progress',
    style: {
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: '0.02em',
      color: 'var(--color-text-secondary)',
    },
    desc: '11px · 500 · +0.02em',
  },
  {
    role: 'Button Label',
    sample: 'Resume Learning',
    style: {
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: '-0.005em',
      color: 'var(--color-text)',
    },
    desc: '14px · 600 · −0.005em',
  },
];

// ─── Spacing block ────────────────────────────────────────────────────────────

function SpacingBlock({ token, px }: { token: string; px: number }) {
  return (
    <div className="flex items-center gap-3">
      <div
        style={{
          width: px,
          height: 20,
          borderRadius: 4,
          background: 'color-mix(in srgb, var(--color-accent) 20%, transparent)',
          border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}
      >
        {token} — {px}px
      </span>
    </div>
  );
}

// ─── Radius block ─────────────────────────────────────────────────────────────

function RadiusBlock({ token, value, label }: { token: string; value: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: value,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-surface-border)',
          backdropFilter: 'blur(12px)',
        }}
      />
      <div className="text-center">
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          {label}
        </p>
        <p
          style={{
            fontSize: 10,
            fontWeight: 400,
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {token}
        </p>
      </div>
    </div>
  );
}

// ─── Shadow block ─────────────────────────────────────────────────────────────

function ShadowBlock({ label, shadow }: { label: string; shadow: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        style={{
          width: 80,
          height: 56,
          borderRadius: 12,
          background: 'var(--color-surface-solid)',
          border: '1px solid var(--color-surface-border)',
          boxShadow: shadow,
        }}
      />
      <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)' }}>{label}</p>
    </div>
  );
}

// ─── Font option toggle ────────────────────────────────────────────────────────

type FontOption = 1 | 2;

const FONT_OPTIONS: { id: FontOption; label: string; desc: string; displayFont: string }[] = [
  {
    id: 1,
    label: 'Option 1',
    desc: 'Plus Jakarta Sans',
    displayFont: 'var(--font-sans)',
  },
  {
    id: 2,
    label: 'Option 2',
    desc: 'Instrument Sans',
    displayFont:
      "'Instrument Sans Variable', 'Instrument Sans', ui-sans-serif, system-ui, sans-serif",
  },
];

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function FoundationsTab() {
  const [fontOption, setFontOption] = useState<FontOption>(1);

  const activeFont = FONT_OPTIONS.find((o) => o.id === fontOption)!;

  return (
    <>
      {/* Colours */}
      <Section label="Colour Palette">
        <div className="flex flex-col gap-6">
          <SwatchGroup
            label="Surfaces"
            swatches={[
              { token: '--color-bg', label: 'Background' },
              { token: '--color-surface-solid', label: 'Raised' },
              { token: '--color-surface-muted', label: 'Sunken' },
              { token: '--color-surface', label: 'Surface' },
            ]}
          />
          <SwatchGroup
            label="Text"
            swatches={[
              { token: '--color-text', label: 'Primary' },
              { token: '--color-text-secondary', label: 'Secondary' },
              { token: '--color-text-muted', label: 'Muted' },
            ]}
          />
          <SwatchGroup
            label="Accent & Status"
            swatches={[
              { token: '--color-accent', label: 'Accent' },
              { token: '--color-success', label: 'Success' },
              { token: '--color-error', label: 'Error' },
              { token: '--color-warning', label: 'Warning' },
              { token: '--color-info', label: 'Info' },
            ]}
          />
          <SwatchGroup
            label="Borders"
            swatches={[
              { token: '--color-border', label: 'Border' },
              { token: '--color-border-strong', label: 'Strong' },
              { token: '--color-surface-border', label: 'Surface' },
            ]}
          />
        </div>
      </Section>

      {/* Typography — type scale */}
      <Section label="Type Scale">
        {/* Font option toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 28,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              marginRight: 4,
            }}
          >
            Display font
          </span>
          {FONT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFontOption(opt.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 9999,
                border:
                  fontOption === opt.id
                    ? '1.5px solid var(--color-accent)'
                    : '1.5px solid var(--color-border)',
                background: fontOption === opt.id ? 'var(--color-accent-subtle)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: fontOption === opt.id ? 600 : 500,
                  color:
                    fontOption === opt.id
                      ? 'var(--color-accent-text)'
                      : 'var(--color-text-secondary)',
                  letterSpacing: '-0.005em',
                }}
              >
                {opt.label}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color:
                    fontOption === opt.id ? 'var(--color-accent-text)' : 'var(--color-text-muted)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {opt.desc}
              </span>
            </button>
          ))}
        </div>

        {/* Type specimens */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {TYPE_SCALE.map((step) => {
            const resolvedFont = step.useDisplay ? activeFont.displayFont : 'var(--font-sans)';
            return (
              <div
                key={step.label}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 20,
                  paddingBottom: 16,
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                {/* Specimen text */}
                <span
                  style={{
                    fontFamily: resolvedFont,
                    fontSize: step.size,
                    fontWeight: step.weight,
                    letterSpacing: step.tracking,
                    lineHeight: step.leading,
                    color: 'var(--color-text)',
                    minWidth: 180,
                    flexShrink: 0,
                  }}
                >
                  {step.label}
                </span>

                {/* Spec line */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <code
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'var(--color-accent-text)',
                      fontFamily: 'var(--font-mono)',
                      background: 'var(--color-accent-subtle)',
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    {step.token}
                  </code>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'var(--color-text-muted)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {step.weight} · {step.tracking} · {step.leading}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {step.role}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Font direction rationale */}
        <div
          style={{
            marginTop: 24,
            padding: '14px 16px',
            borderRadius: 10,
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-border)',
          }}
        >
          {fontOption === 1 ? (
            <>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  marginBottom: 4,
                }}
              >
                Option 1 — Plus Jakarta Sans
              </p>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.6,
                }}
              >
                Warm, friendly geometric sans used throughout. Clean and consistent. Best choice if
                Formency leans warmer and more approachable.
              </p>
            </>
          ) : (
            <>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  marginBottom: 4,
                }}
              >
                Option 2 — Instrument Sans (headings) + Plus Jakarta Sans (body)
              </p>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.6,
                }}
              >
                Instrument Sans brings slightly tighter, more refined proportions to headings — a
                fraction more sophisticated and less consumer-app than PJS at display sizes. Body
                text stays on Plus Jakarta Sans for warmth and readability.
              </p>
            </>
          )}
        </div>
      </Section>

      {/* Product text roles */}
      <Section label="Product Text Roles">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {PRODUCT_ROLES.map((item) => (
            <div
              key={item.role}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr auto',
                alignItems: 'center',
                gap: 20,
                padding: '14px 0',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-muted)',
                }}
              >
                {item.role}
              </span>
              <span style={item.style}>{item.sample}</span>
              <code
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: 'var(--color-text-muted)',
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {item.desc}
              </code>
            </div>
          ))}
        </div>
      </Section>

      {/* Spacing */}
      <Section label="Spacing Scale">
        <div className="flex flex-col gap-2">
          <SpacingBlock token="--space-1" px={4} />
          <SpacingBlock token="--space-2" px={8} />
          <SpacingBlock token="--space-3" px={12} />
          <SpacingBlock token="--space-4" px={18} />
          <SpacingBlock token="--space-5" px={22} />
          <SpacingBlock token="--space-6" px={28} />
          <SpacingBlock token="--space-8" px={36} />
          <SpacingBlock token="--space-10" px={44} />
          <SpacingBlock token="--space-12" px={56} />
          <SpacingBlock token="--space-16" px={72} />
        </div>
      </Section>

      {/* Radius */}
      <Section label="Border Radius">
        <div className="flex flex-wrap gap-5">
          <RadiusBlock token="--radius-sm" value="6px" label="sm" />
          <RadiusBlock token="--radius-md" value="10px" label="md" />
          <RadiusBlock token="--radius-lg" value="16px" label="lg" />
          <RadiusBlock token="--radius-xl" value="20px" label="xl" />
          <RadiusBlock token="--radius-2xl" value="28px" label="2xl" />
          <RadiusBlock token="--radius-full" value="9999px" label="full" />
        </div>
      </Section>

      {/* Shadows */}
      <Section label="Shadows">
        <div className="flex flex-wrap gap-6">
          <ShadowBlock label="sm" shadow="var(--shadow-sm)" />
          <ShadowBlock label="md" shadow="var(--shadow-md)" />
          <ShadowBlock label="lg" shadow="var(--shadow-lg)" />
          <ShadowBlock label="accent" shadow="var(--shadow-accent)" />
        </div>
      </Section>
    </>
  );
}
